import {Form} from 'antd';
import React, {RefObject, useCallback, useState} from 'react';

import {IParametersFilterRef} from 'components/@common/widgets/parameters-filter/parameters-filter';
import {CustomSelect} from 'components/form/inputs/custom-select';
import { ERequestType } from 'shared/types/requests';

import {IOpenInterfaceParametersFilterForm} from '../model/types/open-interface-types';

interface IUseGenerateFiltersProps {
    parametersFilterRef: RefObject<IParametersFilterRef<IOpenInterfaceParametersFilterForm>>;
    shouldRefetchScenarios: boolean;
    setShouldRefetchScenarios: (value: boolean) => void;
}

/**
 * Хук для генерации фильтров открытого интерфейса
 *
 * Создает элементы формы для фильтрации открытого интерфейса.
 * При изменении сценария и ID загрузки сбрасываются зависимые поля.
 *
 * @param parametersFilterRef - Ссылка на компонент фильтра параметров
 * @returns Объект с функциями и состоянием фильтров
 * @returns.generateFilters - Функция для рендеринга JSX элементов фильтров
 * @returns.scenarioId - Текущий выбранный ID сценария
 * @returns.loadId - Текущий выбранный ID загрузки
 * @returns.setScenarioId - Функция для установки ID сценария
 * @returns.setLoadId - Функция для установки ID загрузки
 * @param shouldRefetchScenarios - Флаг для принудительного рефетча сценариев
 * @param setShouldRefetchScenarios - Функция для установки флага для принудительного рефетча сценариев
 */
export const useGenerateFilters = ({
    parametersFilterRef,
    shouldRefetchScenarios,
    setShouldRefetchScenarios,
}: IUseGenerateFiltersProps) => {
    const [scenarioId, setScenarioId] = useState<number| undefined>();
    const [loadId, setLoadId] = useState<number | undefined>();

    const generateFilters = useCallback(
        ({isFilterApplied}: { isFilterApplied: boolean }) => (
            <>
                <Form.Item
                    key="scenarioId"
                    label="Сценарий загрузки"
                    name="scenarioId"
                    rules={[
                        {
                            required: true,
                            message: 'Пожалуйста, заполните поле сценарий загрузки',
                        },
                    ]}
                    required
                    data-type="lov"
                    className="field-wide"
                >
                    <CustomSelect
                        onChange={value => {
                            setScenarioId(value);
                            setLoadId(undefined);

                            parametersFilterRef.current?.setFieldValue('loadId', undefined);
                            parametersFilterRef.current?.setFieldValue('srcId', undefined);
                        }}
                        fixedDropdown={false}
                        settings={{
                            placeholder: 'Выберите сценарий',
                            url: 'open-interface/get-scenario',
                            labelPath: 'description',
                            valuePath: 'id',
                            isDisabled: isFilterApplied,
                            isClearable: true,
                            requestType: ERequestType.POST,
                            fetchTrigger: shouldRefetchScenarios,
                        }}
                        setShouldRefetchData={setShouldRefetchScenarios}
                    />
                </Form.Item>
                <Form.Item
                    key="loadId"
                    label="ID загрузки"
                    name="loadId"
                    rules={[
                        {
                            required: true,
                            message: 'Пожалуйста, заполните поле номер загрузки',
                        },
                    ]}
                    required
                    data-type="lov"
                >
                    <CustomSelect
                        onChange={value => {
                            setLoadId(value);

                            if (!value) {
                                parametersFilterRef.current?.setFieldValue('srcId', undefined);
                            }
                        }}
                        fixedDropdown={false}
                        settings={{
                            placeholder: 'Выберите номер загрузки',
                            url: scenarioId
                                ? `open-interface/get-load?scenarioId=${scenarioId}`
                                : '',
                            labelPath: 'loadId',
                            valuePath: 'loadId',
                            isDisabled: isFilterApplied || !scenarioId,
                            isClearable: true,
                            requestType: ERequestType.POST,
                        }}
                    />
                </Form.Item>
                <Form.Item
                    key="srcId"
                    label="Система-источник"
                    name="srcId"
                    data-type="lov"
                >
                    <CustomSelect
                        settings={{
                            placeholder: 'Выберите систему-источник',
                            url:
                scenarioId && loadId
                    ? `open-interface/get-src?scenarioId=${scenarioId}&loadId=${loadId}`
                    : '',
                            labelPath: 'description',
                            valuePath: 'id',
                            isDisabled: isFilterApplied || !loadId || !scenarioId,
                            isClearable: true,
                            requestType: ERequestType.POST,
                        }}
                    />
                </Form.Item>
                <Form.Item
                    key="loadId"
                    label="Статус данных"
                    name="loadId"
                    data-type="lov"
                    className="field-wide"
                >
                    <CustomSelect
                        settings={{
                            placeholder: 'Не определено',
                            url: scenarioId
                                ? `open-interface/get-load?scenarioId=${scenarioId}`
                                : '',
                            labelPath: 'statusName',
                            valuePath: 'loadId',
                            isDisabled: true,
                            isClearable: true,
                            requestType: ERequestType.POST,
                        }}
                    />
                </Form.Item>
            </>
        ),
        [parametersFilterRef, scenarioId, loadId, shouldRefetchScenarios, setShouldRefetchScenarios],
    );

    return {
        generateFilters, scenarioId, loadId, setScenarioId, setLoadId,
    };
};
